<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { zoomedRect, zoomedViewport } from './cssZoom';

    let {
        help = '',
        textSize = 13,
        width = 400,
        size = 22,
        preLine = false
    }: {
        help: string;
        /** Popup text size in px (the icon itself is unaffected). */
        textSize?: number;
        /** Popup width in px. Also drives the left/right flip threshold. */
        width?: number;
        /** Diameter of the icon's circle in px, for tight spots like a small caption. */
        size?: number;
        /** Show line breaks in `help` (a blank line becomes a paragraph break). */
        preLine?: boolean;
    } = $props();

    // The question-mark glyph keeps the default's 16-in-22 proportion.
    let glyph = $derived(Math.round(size * 16 / 22));

    let iconElement: HTMLDivElement | null = $state(null);
    let popupElement: HTMLDivElement | null = $state(null);
    let showLeft = $state(false);
    let popupTop = $state(0);
    let popupLeft = $state(0);
    let arrowTop = $state(0);
    let maxHeight = $state(0);

    /** Gap between the icon and the popup, and the margin kept off every viewport edge. */
    const GAP = 12;

    /**
     * The popup is `position: fixed` rather than absolutely placed next to the icon.
     * Absolute placement is clipped by any scrolling ancestor — a grid header cell, for
     * one — and z-index cannot escape an `overflow: hidden`, so a long explanation lost
     * its bottom. Fixed takes it out of that flow, at the cost of having to compute the
     * coordinates here. InputGrid's cell tooltip is placed the same way.
     *
     * Measurements go through cssZoom: px written to an element's own style resolve in
     * that element's coordinate space, which an app-level zoom scales a second time.
     */
    function updatePosition() {
        if (!iconElement) return;

        const rect = zoomedRect(iconElement, popupElement);
        const viewport = zoomedViewport(popupElement);

        showLeft = viewport.width - rect.right < width + GAP * 2;
        const wanted = showLeft ? rect.left - width - GAP : rect.right + GAP;
        popupLeft = Math.max(GAP, Math.min(wanted, viewport.width - width - GAP));

        // Never taller than the viewport; the content scrolls if it would be.
        maxHeight = viewport.height - GAP * 2;
        const height = Math.min(popupElement?.scrollHeight ?? 0, maxHeight);

        // Centred on the icon, then pulled back inside the viewport so the last line
        // cannot fall off the bottom edge.
        const iconCenter = rect.top + rect.height / 2;
        popupTop = Math.max(GAP, Math.min(iconCenter - height / 2, viewport.height - height - GAP));

        // Once clamped the popup is no longer centred on the icon, so the arrow has to
        // track the icon itself or it points at nothing.
        arrowTop = Math.max(8, Math.min(iconCenter - popupTop, Math.max(8, height - 8)));
    }

    onMount(() => {
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
    });

    onDestroy(() => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
    });
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="help-icon-wrapper" bind:this={iconElement} onmouseenter={updatePosition} style="width: {size}px; height: {size}px">
    <svg
        class="help-icon"
        width={glyph}
        height={glyph}
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
        <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286m1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94" />
    </svg>
    <div
        class="help-popup"
        bind:this={popupElement}
        style="font-size: {textSize}px; width: {width}px; top: {popupTop}px; left: {popupLeft}px; max-height: {maxHeight}px{preLine ? '; white-space: pre-line' : ''}"
    >
        {help}
        <div class="arrow {showLeft ? 'arrow-right' : 'arrow-left'}" style="top: {arrowTop}px"></div>
    </div>
</div>

<style>
    .help-icon-wrapper {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 6px;
        background-color: var(--color-primary);
        border: 1px solid var(--color-primary);
        border-radius: 50%;
        width: 22px;
        height: 22px;
        padding: 0;
        cursor: help;
        transition: background-color 200ms ease-in-out, color 200ms ease-in-out;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }

    .help-icon-wrapper:hover {
        background-color: white;
    }

    .help-icon {
        color: white;
        transition: color 200ms ease-in-out;
        flex-shrink: 0;
    }

    .help-icon-wrapper:hover .help-icon {
        color: var(--color-primary);
    }

    /* Fixed, not absolute — see updatePosition. top/left/width/max-height are all
       written inline from there. */
    .help-popup {
        position: fixed;
        background: var(--color-primary);
        color: white;
        padding: 10px 14px;
        border-radius: 6px;
        /* Font size and width come from props (inline style); 13px / 400px default. */
        line-height: 1.5;
        overflow-y: auto;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        opacity: 0;
        pointer-events: none;
        transition: opacity 200ms ease-in-out;
        white-space: normal;
        word-wrap: break-word;
    }

    .help-icon-wrapper:hover .help-popup {
        opacity: 1;
    }

    .arrow {
        position: absolute;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
    }

    .arrow-left {
        left: -6px;
        border-right: 6px solid var(--color-primary);
    }

    .arrow-right {
        right: -6px;
        border-left: 6px solid var(--color-primary);
    }
</style>
